import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Drawer,
  IconButton,
  Divider,
  Grid,
  Chip,
} from '@mui/material';
import { OptionsHeaderBar } from '../../components/options/OptionsHeaderBar';
import { useOptionsStore } from '../../store/useOptionsStore';
import CloseIcon from '@mui/icons-material/Close';

const OptionChainPage: React.FC = () => {
  const {
    expiries,
    selectedExpiry,
    chainData,
    spotData,
    isLoading,
    fetchExpiries,
    fetchChainData,
    selectContract,
    selectedContract,
    isInspectorOpen,
    closeInspector,
  } = useOptionsStore();
  const [expiry, setExpiry] = useState(selectedExpiry || '');

  useEffect(() => {
    fetchExpiries();
  }, []);

  useEffect(() => {
    if (!expiry && expiries.length > 0) {
      setExpiry(expiries[0]);
    }
  }, [expiries]);

  useEffect(() => {
    if (expiry) {
      fetchChainData(expiry);
    }
    const timer = setInterval(() => {
      if (expiry) {
        fetchChainData(expiry);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [expiry]);

  const spot = chainData?.spot || spotData?.spot || 0;
  const atmStrike = chainData?.atmStrike || (spot > 0 ? Math.round(spot / 50) * 50 : 0);

  const strikesToRender = chainData?.strikes && chainData.strikes.length > 0 ? chainData.strikes : [];

  return (
    <Box sx={{ p: 3, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <OptionsHeaderBar />

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
        {/* Controls */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
              NIFTY 50 OPTION CHAIN
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              Spot: {spot > 0 ? `₹${spot.toFixed(1)}` : '—'} • ATM: {atmStrike || '—'} {chainData?.pcr ? `• PCR: ${chainData.pcr.toFixed(2)}` : ''} {chainData?.maxPain ? `• Max Pain: ${chainData.maxPain}` : ''} • Click any contract for Greek & Order Book Inspector
            </Typography>
          </Box>

          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
              Expiry:
            </Typography>
            <Select
              size="small"
              value={typeof expiry === 'string' && expiry ? expiry.substring(0, 10) : (expiries.length > 0 ? (typeof expiries[0] === 'string' ? expiries[0].substring(0, 10) : '') : '')}
              onChange={(e) => {
                const newExp = String(e.target.value);
                setExpiry(newExp);
                fetchChainData(newExp);
              }}
              sx={{ minWidth: 140, height: 34 }}
            >
              {expiries.map((exp) => {
                const expVal = typeof exp === 'string' ? exp.substring(0, 10) : ((exp as any)?.expiryDate ? String((exp as any).expiryDate).substring(0, 10) : String(exp));
                return (
                  <MenuItem key={expVal} value={expVal}>
                    {expVal}
                  </MenuItem>
                );
              })}
            </Select>
          </Stack>
        </Stack>

        {/* Matrix Table */}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ fontFamily: "'Roboto Mono', monospace" }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#0F172A', '& th': { color: '#FFFFFF', fontWeight: 700, fontSize: '0.72rem' } }}>
                <TableCell align="center" colSpan={6} sx={{ bgcolor: '#1E293B', borderRight: '1px solid #334155' }}>
                  CALLS
                </TableCell>
                <TableCell align="center" sx={{ bgcolor: '#0284C7' }}>
                  STRIKE
                </TableCell>
                <TableCell align="center" colSpan={6} sx={{ bgcolor: '#1E293B', borderLeft: '1px solid #334155' }}>
                  PUTS
                </TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: '#F1F5F9', '& th': { color: '#475569', fontWeight: 600, fontSize: '0.7rem' } }}>
                <TableCell align="right">OI</TableCell>
                <TableCell align="right">Chg OI</TableCell>
                <TableCell align="right">Vol</TableCell>
                <TableCell align="right">IV%</TableCell>
                <TableCell align="right">Delta</TableCell>
                <TableCell align="right" sx={{ borderRight: '1px solid #E2E8F0' }}>
                  LTP
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#E0F2FE' }}>
                  STRIKE
                </TableCell>
                <TableCell align="left" sx={{ borderLeft: '1px solid #E2E8F0' }}>
                  LTP
                </TableCell>
                <TableCell align="right">Delta</TableCell>
                <TableCell align="right">IV%</TableCell>
                <TableCell align="right">Vol</TableCell>
                <TableCell align="right">Chg OI</TableCell>
                <TableCell align="right">OI</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {strikesToRender.length === 0 && (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 4, color: '#64748B' }}>
                    {isLoading ? 'Loading live option chain…' : 'No live option chain data available for this expiry'}
                  </TableCell>
                </TableRow>
              )}
              {strikesToRender.map((strike) => {
                const call = chainData?.calls?.[strike];
                const put = chainData?.puts?.[strike];
                const isAtm = strike === atmStrike;

                return (
                  <TableRow
                    key={strike}
                    hover
                    sx={{
                      bgcolor: isAtm ? 'rgba(2, 132, 199, 0.08)' : strike < atmStrike ? '#FAFAF9' : '#FFFFFF',
                      '& td': { fontSize: '0.75rem', py: 0.6 },
                    }}
                  >
                    {/* Calls */}
                    <TableCell align="right">{call?.oi ? (call.oi / 100000).toFixed(1) + 'L' : '-'}</TableCell>
                    <TableCell align="right" sx={{ color: (call?.oiChange || 0) >= 0 ? '#16A34A' : '#DC2626' }}>
                      {call?.oiChange !== undefined ? ((call.oiChange >= 0 ? '+' : '') + (call.oiChange / 1000).toFixed(0) + 'k') : '-'}
                    </TableCell>
                    <TableCell align="right">{call?.volume ? (call.volume / 1000).toFixed(0) + 'k' : '-'}</TableCell>
                    <TableCell align="right">{call?.iv ? `${call.iv}%` : '-'}</TableCell>
                    <TableCell align="right">{call?.delta !== undefined ? call.delta : '-'}</TableCell>
                    <TableCell
                      align="right"
                      onClick={() => call && selectContract(call)}
                      sx={{ fontWeight: 700, color: '#0284C7', cursor: call ? 'pointer' : 'default', borderRight: '1px solid #E2E8F0' }}
                    >
                      {call ? `₹${call.ltp.toFixed(2)}` : '-'}
                    </TableCell>

                    {/* Strike */}
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        bgcolor: isAtm ? '#0284C7' : '#F1F5F9',
                        color: isAtm ? '#FFFFFF' : '#0F172A',
                      }}
                    >
                      {strike} {isAtm && '• ATM'}
                    </TableCell>

                    {/* Puts */}
                    <TableCell
                      align="left"
                      onClick={() => put && selectContract(put)}
                      sx={{ fontWeight: 700, color: '#0284C7', cursor: put ? 'pointer' : 'default', borderLeft: '1px solid #E2E8F0' }}
                    >
                      {put ? `₹${put.ltp.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell align="right">{put?.delta !== undefined ? put.delta : '-'}</TableCell>
                    <TableCell align="right">{put?.iv ? `${put.iv}%` : '-'}</TableCell>
                    <TableCell align="right">{put?.volume ? (put.volume / 1000).toFixed(0) + 'k' : '-'}</TableCell>
                    <TableCell align="right" sx={{ color: (put?.oiChange || 0) >= 0 ? '#16A34A' : '#DC2626' }}>
                      {put?.oiChange !== undefined ? ((put.oiChange >= 0 ? '+' : '') + (put.oiChange / 1000).toFixed(0) + 'k') : '-'}
                    </TableCell>
                    <TableCell align="right">{put?.oi ? (put.oi / 100000).toFixed(1) + 'L' : '-'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* Contract Inspector Drawer */}
      <Drawer anchor="right" open={isInspectorOpen} onClose={closeInspector}>
        <Box sx={{ width: 360, p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
              CONTRACT INSPECTOR
            </Typography>
            <IconButton onClick={closeInspector} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>

          {selectedContract && (
            <Stack spacing={2.5}>
              <Box sx={{ p: 2, bgcolor: '#0F172A', color: '#FFFFFF', borderRadius: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  NIFTY {selectedContract.strike} {selectedContract.optionType}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#38BDF8', fontFamily: "'Roboto Mono', monospace", mt: 0.5 }}>
                  ₹{selectedContract.ltp.toFixed(2)}
                </Typography>
              </Box>

              {/* Greeks */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                  OPTION GREEKS
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 6 }}>
                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        Delta (Δ)
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {selectedContract.delta}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        Gamma (Γ)
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {selectedContract.gamma}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        Theta (Θ)
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#DC2626' }}>
                        {selectedContract.theta}/d
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        Vega (ν)
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {selectedContract.vega}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Order Depth */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                  ORDER DEPTH & SPREAD
                </Typography>
                <Stack direction="row" justifyContent="space-between" sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 1 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      Bid
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#16A34A' }}>
                      ₹{selectedContract.bid}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      Ask
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#DC2626' }}>
                      ₹{selectedContract.ask}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default OptionChainPage;
